import * as React from 'react'
import DataTable, { ExpanderComponentProps, TableColumn } from 'react-data-table-component'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DateSelectArg, EventClickArg, EventContentArg } from '@fullcalendar/core/index.js'

import { useAuth } from '../hooks/useAuth'
import useDealFetcher from '../hooks/useDealFetcher'
import { fechaDateToFull, fechaIsoToFull, fechaIsoToTime } from '../utils/utils'
import DealDto, { Activity } from '../interfaces/DealDto'
import { makeSmallImage } from '../utils/makeImage'
import { AppPath } from '../enums/AppPath'
import { transActivityStatus } from '../utils/activityStatusTranslate'
import useFilteredData from '../hooks/useFilteredData'
import activityTypeTranslate from '../utils/activityTypeTranslate'
import filterContains from '../utils/filterContains'
import { VehicleBasePic } from '../interfaces/Vehicle'
import ClientBase from '../interfaces/ClientBase'
import { ScreenWrapper } from '../modules/common/components/ScreenWrapper'
import { useDealsFilters } from '../hooks/useDealsFilters'

import DealChart from './DealChart'
import DealVistaSelector from './DealVistaSelector'
import CardOneSearch from './CardOneSearch'
import { ButtonMenuSet } from './Buttons'
import { DealStatus } from './DealStatus'

const dateSort = (rowA: DealDto, rowB: DealDto) => {
  const a = rowA.lastCommentSortDate
  const b = rowB.lastCommentSortDate

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}
const clientSort = (rowA: DealDto, rowB: DealDto) => {
  const a = `${rowA.client?.firstname ?? ''} ${rowA.client?.lastname ?? ''}`
  const b = `${rowB.client?.firstname ?? ''} ${rowB.client?.lastname ?? ''}`

  if (a.toUpperCase() > b.toUpperCase()) {
    return 1
  }

  if (b.toUpperCase() > a.toUpperCase()) {
    return -1
  }

  return 0
}
const userSort = (rowA: DealDto, rowB: DealDto) => {
  const a = `${rowA.user?.firstname ?? ''} ${rowA.user?.lastname ?? ''}`
  const b = `${rowB.user?.firstname ?? ''} ${rowB.user?.lastname ?? ''}`

  if (a.toUpperCase() > b.toUpperCase()) {
    return 1
  }

  if (b.toUpperCase() > a.toUpperCase()) {
    return -1
  }

  return 0
}

const createdAtSort = (rowA: DealDto, rowB: DealDto) => {
  const a = rowA.createdAt
  const b = rowB.createdAt

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

const buttonCaptions = ['En Curso', 'Completados', 'Cancelados']

const toolBarInput = {
  left: 'prev,next today',
  center: 'title',
  right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
}

// a custom render function para mostrar el detalle
const renderEventContent = (eventInfo: EventContentArg) => {
  const activity = eventInfo.event._def.extendedProps.activity
  const vehicle = activity.vehicle
  const client = activity.client
  const user = activity.user
  const mytime = fechaIsoToTime(activity.date)
  const additionalComments = eventInfo.event.title !== activity.body ? activity.body : ''

  return (
    <div>
      <div className="font-bold flex gap-1">
        <div>{mytime}</div>

        <div>{eventInfo.event.title}</div>
      </div>
      <div>{additionalComments}</div>
      <div>{`Cliente: ${client.firstname} ${client.lastname}`}</div>
      <div> {`${vehicle.year}  ${vehicle.brand} ${vehicle.model} ${vehicle.version ?? ''} [${vehicle.plate}] `}</div>
      <div>{`Usuario: ${user.firstname} ${user.lastname}`}</div>
    </div>
  )
}

// Las dos siguientes tienen que estar FUERA de la componente
const transActivityType = (type: string | undefined) => {
  if (!type) {
    type = 'TYPE.OTHER'
  }

  return activityTypeTranslate[type] ?? 'Actividad'
}
const getTitle = (act: Activity) => {
  if (act.type === 'TYPE.OTHER' || !act.type) {
    return act.body
  }

  return transActivityType(act.type)
}
// Fin de "Las dos ..."

const Deals: React.FC = () => {
  const [deals, setDeals] = useState<DealDto[]>([])
  const { userData } = useAuth()
  const { fetchDeals } = useDealFetcher()
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const myRef = useRef<HTMLDivElement | null>(null)

  // Use global filter state
  const { filters, updateFilters } = useDealsFilters()
  const { clickedIndex, filterData, searchTerm, statusFilter } = filters

  // Vendor dropdown state
  const [vendorOptions, setVendorOptions] = useState<string[]>([])

  // Extract unique vendors from deals
  const getUniqueVendors = (deals: DealDto[]) => {
    const vendorSet = new Set<string>()

    deals.forEach((deal) => {
      if (deal.user?.firstname || deal.user?.lastname) {
        const fullName = `${deal.user.firstname ?? ''} ${deal.user.lastname ?? ''}`.trim()

        if (fullName) {
          vendorSet.add(fullName)
        }
      }
    })

    return Array.from(vendorSet).sort() // Alphabetical order
  }

  const columns: TableColumn<DealDto>[] = [
    {
      name: 'Usuario',
      selector: (row) => `${row.user?.firstname ?? ''} ${row.user?.lastname ?? ''}`,
      width: '140px',
      sortable: true,
      sortFunction: userSort,
    },
    {
      name: 'Cliente',
      selector: (row) => `${row.client?.firstname ?? ''} ${row.client?.lastname ?? ''}`,
      width: '180px',
      sortable: true,
      sortFunction: clientSort,
      wrap: true,
    },
    {
      name: 'Teléfono',
      selector: (row) => row.client?.phone ?? '',
      width: '120px',
      sortable: true,
      cell: (row) => <span className="font-mono text-sm">{row.client?.phone || '-'}</span>,
    },
    {
      name: 'Estado',
      cell: (row) => <DealStatus status={row.status || 'ontime'} />,
      width: '100px',
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const statusA = rowA.status || 'ontime'
        const statusB = rowB.status || 'ontime'

        if (statusA === statusB) return 0

        return statusA === 'late' ? 1 : -1
      },
    },
    {
      name: 'Miniatura',
      grow: 0,
      width: '80px',
      cell: (row) => (
        <Link to={`/${AppPath.Deals}/view/${row._id}`}>
          <img
            width="64px"
            alt={row.vehicle?.brand ?? ''}
            src={makeSmallImage(row.vehicle?.pictures?.public?.[0] ?? '')}
          />
        </Link>
      ),
    },
    {
      name: 'Vehículo',
      selector: (row) =>
        `${row.vehicle?.brand} ${row.vehicle?.model} ${row.vehicle?.version ?? ''} (${row.vehicle?.year}) [${row.vehicle?.plate}]`,
      width: '300px',
      sortable: true,
      wrap: true,
    },

    {
      name: 'Fecha de creación',
      selector: (row) => fechaIsoToFull(row.createdAt),
      width: '160px',
      sortable: true,
      sortFunction: createdAtSort,
    },
    {
      name: 'Actividad',
      selector: (row) => row.lastCommentBody,
      width: '280px',
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '280px',
          }}
          title={row.lastCommentBody}
        >
          {row.lastCommentBody}
        </div>
      ),
    },
    {
      name: 'Fecha agendada',
      selector: (row) => fechaIsoToFull(row.lastCommentSortDate),
      width: '160px',
      sortable: true,
      sortFunction: dateSort,
    },
  ]

  useEffect(() => {
    if (!userData) {
      return
    }
    fetchAsync(userData?.organization?._id ?? '', clickedIndex)
  }, [clickedIndex, userData])

  // Update vendor options when deals change
  useEffect(() => {
    const uniqueVendors = getUniqueVendors(deals)
    setVendorOptions(uniqueVendors)
  }, [deals])

  const fetchAsync = async (orgId: string, status: number) => {
    const deals: DealDto[] = await fetchDeals(orgId, status)

    if (!deals) {
      return
    }

    setDeals(deals)
  }

  const navigate = useNavigate()

  const onRowClickedHandler = (row: DealDto): void => {
    const url = `/${AppPath.Deals}/view/${row._id}`
    navigate(url)
  }

  const handleEventClick = (clickInfo: EventClickArg) => {
    const activity = clickInfo.event._def.extendedProps.activity

    const url = `/${AppPath.Deals}/view/${activity.deal}`
    navigate(url)
  }
  // Click en area vacia del calendario
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    setIsPopupVisible(!isPopupVisible)
    setPosition({ x: selectInfo.jsEvent?.pageX ?? 0, y: selectInfo.jsEvent?.pageY ?? 0 })
  }
  const handleModificar = () => {
    setIsPopupVisible(false) // Hide popup after selecting an option

    if (myRef.current) {
      const element = myRef.current
      const elementRect = element.getBoundingClientRect()
      const absoluteElementTop = elementRect.top + window.pageYOffset
      const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2

      // Scroll to the calculated middle position
      window.scrollTo({
        top: middle,
        behavior: 'smooth',
      })
    }
  }

  // Para la busqueda
  const array = deals
  let filtered = useFilteredData({
    array,
    filterFields: [
      (item) => item.client?.firstname ?? '',
      (item) => item.client?.lastname ?? '',
      (item) => item.vehicle?.brand ?? '',
      (item) => item.vehicle?.model ?? '',
      (item) => item.vehicle?.version ?? '',
      (item) => item.vehicle?.plate ?? '',
    ],
    allowFilter: true,
    searchTerm,
  })

  if (filterData) {
    if (filterData.salesPerson) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => `${item.user?.firstname ?? ''} ${item.user?.lastname ?? ''}`,
        searchTerm: filterData.salesPerson ?? '',
      })
    }

    if (filterData.dateSince) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => item.createdAt,
        dateSince: filterData.dateSince,
      })
    }

    if (filterData.dateUntil) {
      filtered = filterContains({
        list: filtered,
        filterField: (item) => item.createdAt,
        dateUntil: filterData.dateUntil,
      })
    }
  }

  // Calculate counts before applying status filter
  const totalDeals = filtered.length
  const lateDeals = filtered.filter((deal) => deal.status === 'late').length
  const onTimeDeals = filtered.filter((deal) => deal.status === 'ontime').length

  // Apply status filter
  if (statusFilter !== 'all') {
    filtered = filtered.filter((deal) => deal.status === statusFilter)
  }

  // Armemos los eventos para mostrar en el calendario

  // Debug: Check what deals and comments look like
  console.log('🔍 Total filtered deals:', filtered.length)

  if (filtered.length > 0) {
    console.log('🔍 Sample deal full structure:', JSON.stringify(filtered[0], null, 2))
    console.log('🔍 Deal has comments field?', 'comments' in filtered[0])
    console.log('🔍 Comments array:', filtered[0].comments)
    console.log('🔍 Comments type:', typeof filtered[0].comments)
    console.log('🔍 Comments length:', filtered[0].comments?.length)
  }

  const dealsWithComments = filtered.filter((deal) => deal.comments?.length > 0)
  console.log('🔍 Deals with comments:', dealsWithComments.length)

  // Filter out DealDto objects with empty comments and flatten the structure
  const eventsAll = filtered
    .filter((deal) => deal.comments?.length > 0) // Exclude DealDto with empty comments
    .flatMap((deal) =>
      deal.comments.map((comment) => ({
        ...comment, // Include all properties of the comment
        dealId: deal._id, // Add deal-specific properties
        revisedAt: deal.revisedAt,
        contactedVia: deal.contactedVia,
        vehicle: deal.vehicle,
        client: deal.client,
        lastCommentBody: deal.lastCommentBody,
        lastCommentSortDate: deal.lastCommentSortDate,
        profile: deal.profile,
        user: deal.user,
        createdAt: deal.createdAt,
        actTitle: getTitle(comment),
      }))
    )
  // Mostramos todos los que tienen alguna actividad Agendada
  // O la actividad no tenga estado (casos antiguos)
  // Y para sacar los que no tienen actividades, por algun
  // motivo el filtro de arriba no funciona
  const eventsScheduled = eventsAll.filter(
    (eve) => (eve.status ?? 'STATUS.SCHEDULED') === 'STATUS.SCHEDULED' && eve.deal
  )

  // Obtenemos el titulo a partir del typo de actividad. Si no está,
  // del comentario

  const events = eventsScheduled.map((act) => ({
    id: act._id,
    title: act.actTitle,
    start: new Date(act.date ?? act.createdAt),
    extendedProps: { activity: act },
  }))

  // DEBUG: Check events for chart
  console.log('🔍 Chart Debug - eventsAll:', eventsAll.length)
  console.log('🔍 Chart Debug - eventsScheduled:', eventsScheduled.length)
  console.log('🔍 Chart Debug - events:', events.length)

  // Lo enviamos a la pagina propia de creacion, sin pasar por la VehCliSelection

  const searchCreateClick = () => {
    navigate(`/${AppPath.Deals}/create`)
  }
  const onClearFilter = () => {
    updateFilters({
      filterData: {
        salesPerson: '',
        dateSince: '',
        dateUntil: '',
      },
      searchTerm: '',
      statusFilter: 'all',
    })
  }

  const today = new Date()

  const dataChart = events.map((eve) => ({
    x: eve.extendedProps.activity.vehicle.price / 1000000,
    y: (eve.start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    tooltip: getTooltip(eve.extendedProps.activity.vehicle, eve.extendedProps.activity.client, eve.start),
    id: eve.extendedProps.activity.dealId,
  }))

  //Los atrasados
  let dataChartAtrasados = dataChart.filter((d) => d.y < 0)
  let dataChartAtiempo = dataChart.filter((d) => d.y >= 0)

  // Apply status filter to chart data
  if (statusFilter === 'late') {
    dataChartAtiempo = []
  } else if (statusFilter === 'ontime') {
    dataChartAtrasados = []
  }

  const onChartPointClick = (id: string) => {
    navigate(`/${AppPath.Deals}/view/${id}`)
  }

  // Lo siguiente es para mostrar en la barra de search el selector de la vista
  const [vistaSelected, setVistaSelected] = useState<number>(0)
  const vistaClicked = (sel: number) => {
    setVistaSelected(sel)
  }

  const dealVistaSelector = <DealVistaSelector vistaSelected={vistaSelected} vistaClicked={vistaClicked} />

  return (
    <ScreenWrapper>
      <CardOneSearch
        headerText={'Gestionar '}
        docuType={AppPath.Deals}
        searchTerm={searchTerm}
        setSearchTerm={(term) => updateFilters({ searchTerm: term })}
        onCreateClick={searchCreateClick}
        onClearClick={onClearFilter}
        toolBar={dealVistaSelector}
      />

      {/* Unified Filter and Status Section */}
      <div className="mt-4 mb-4 p-4 bg-gray-50 rounded-lg border">
        {/* Filter Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="font-semibold text-gray-700 text-sm block mb-2">VENDEDOR</label>
            <div className="flex gap-1">
              <select
                onChange={({ target }) => {
                  const updatedFilter = { ...filterData, salesPerson: target.value }
                  updateFilters({ filterData: updatedFilter })
                }}
                value={filterData.salesPerson || ''}
                className="flex-1 border rounded p-2 text-sm bg-white"
                name="vendor-select"
              >
                <option value="">-- Todos los vendedores --</option>
                {vendorOptions.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const updatedFilter = { ...filterData, salesPerson: '' }
                  updateFilters({ filterData: updatedFilter })
                }}
                disabled={!filterData.salesPerson}
                className="px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Limpiar vendedor"
              >
                ✕
              </button>
            </div>
          </div>

          <div>
            <label className="font-semibold text-gray-700 text-sm block mb-2">DESDE</label>
            <div className="flex gap-1">
              <input
                onChange={({ target }) => {
                  const updatedFilter = { ...filterData, dateSince: target.value }
                  updateFilters({ filterData: updatedFilter })
                }}
                className="flex-1 border rounded p-2 text-sm"
                max={filterData.dateUntil?.valueOf()}
                value={filterData.dateSince || ''}
                name="date-since"
                type="date"
                placeholder="dd-mm-yyyy"
              />
              <button
                onClick={() => {
                  const updatedFilter = { ...filterData, dateSince: '' }
                  updateFilters({ filterData: updatedFilter })
                }}
                disabled={!filterData.dateSince}
                className="px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Limpiar fecha desde"
              >
                ✕
              </button>
            </div>
          </div>

          <div>
            <label className="font-semibold text-gray-700 text-sm block mb-2">HASTA</label>
            <div className="flex gap-1">
              <input
                onChange={({ target }) => {
                  const updatedFilter = { ...filterData, dateUntil: target.value }
                  updateFilters({ filterData: updatedFilter })
                }}
                className="flex-1 border rounded p-2 text-sm"
                value={filterData.dateUntil || ''}
                min={filterData.dateSince}
                name="date-until"
                type="date"
                placeholder="dd-mm-yyyy"
              />
              <button
                onClick={() => {
                  const updatedFilter = { ...filterData, dateUntil: '' }
                  updateFilters({ filterData: updatedFilter })
                }}
                disabled={!filterData.dateUntil}
                className="px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Limpiar fecha hasta"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Translucent Separator Line */}
        <div className="border-t border-gray-300 opacity-50 mb-4"></div>

        {/* Status Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
            onClick={() => updateFilters({ statusFilter: 'all' })}
          >
            Todos ({totalDeals})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'late'
                ? 'bg-red-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
            onClick={() => updateFilters({ statusFilter: 'late' })}
          >
            Atrasados ({lateDeals})
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'ontime'
                ? 'bg-green-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
            onClick={() => updateFilters({ statusFilter: 'ontime' })}
          >
            A tiempo ({onTimeDeals})
          </button>
        </div>
      </div>

      <div className="">
        <ButtonMenuSet
          captions={buttonCaptions}
          clickedIndex={clickedIndex}
          handleButtonClick={(n) => updateFilters({ clickedIndex: n })}
        />

        <div className="mt-4">
          {clickedIndex === 0 && (
            <>
              {vistaSelected === 0 && (
                <DealChart
                  dataAtrasado={dataChartAtrasados}
                  dataAtiempo={dataChartAtiempo}
                  onPointClick={onChartPointClick}
                />
              )}
              {vistaSelected === 1 && (
                <div className="h-screen">
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    weekends={true}
                    events={events}
                    eventContent={renderEventContent}
                    headerToolbar={toolBarInput}
                    locale={esLocale}
                    hiddenDays={[0]}
                    //      aspectRatio={2.5}
                    height="90%"
                    selectable={true}
                    //           editable={true}
                    select={handleDateSelect}
                    eventClick={handleEventClick}
                  />
                </div>
              )}
            </>
          )}

          <div ref={myRef}>
            <DataTable
              columns={columns}
              data={filtered}
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              highlightOnHover
              pointerOnHover
              dense
              onRowClicked={onRowClickedHandler}
              customStyles={{
                table: {
                  style: {
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: '0',
                  },
                },
                headCells: {
                  style: {
                    padding: '12px 16px',
                    borderRight: '1px solid #f1f5f9',
                    fontWeight: '600',
                    backgroundColor: '#f8fafc',
                    whiteSpace: 'nowrap',
                  },
                },
                cells: {
                  style: {
                    padding: '12px 16px',
                    borderRight: '1px solid #f1f5f9',
                    borderBottom: '1px solid #f1f5f9',
                  },
                },
                rows: {
                  style: {
                    minHeight: '52px',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Popup Menu */}
      {isPopupVisible && (
        <div
          className="absolute bg-white border border-gray-200 rounded shadow-lg  z-10"
          style={{ top: position.y, left: position.x }}
        >
          <div className="flex flex-col">
            <button className="px-4 py-2 text-left hover:bg-gray-100" onClick={() => searchCreateClick()}>
              Crear negocio
            </button>
            <button className="px-4 py-2 text-left hover:bg-gray-100" onClick={() => handleModificar()}>
              Agregar actividad a negocio existente
            </button>
          </div>
        </div>
      )}
    </ScreenWrapper>
  )
}

const ExpandedComponent: React.FC<ExpanderComponentProps<DealDto>> = ({ data }) => (
  <div className="border mb-8">
    <div className="grid grid-cols-3 ml-20 font-bold">
      <div className="flex">
        <div className="mr-1">Actividad</div>{' '}
      </div>
      <div>Fecha agendada</div>
      <div>Estado</div>
    </div>
    {(data.comments ?? []).map((field) => (
      <div key={field._id} className="grid grid-cols-3 ml-20">
        <div>{field.body}</div>
        <div>{fechaIsoToFull(field.date)}</div>
        <div>{transActivityStatus(field.status)}</div>
      </div>
    ))}
  </div>
)

export default Deals

const getTooltip = (vehicle: VehicleBasePic, client: ClientBase, start: Date) => {
  const tooltip = []
  const veh = `${vehicle.year} ${vehicle.brand} ${vehicle.model}`
  tooltip.push(veh)
  const cli = `${client.firstname} ${client.lastname}`
  tooltip.push(cli)
  const desde = fechaDateToFull(start)
  tooltip.push(desde)

  return tooltip
}
